import { useMutation, useQuery, useQueryClient, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { QData } from "../../../types/basic-type-list";
import { TResponseWithQData } from "../../../types/common";
import { IContactPayload, IDeleteDataResponseAndPayload } from "../../../types/api/contact";
import { deleteContact, listContact } from "./callbacks";



export const useContactList = (
  qData: QData,
  options?: Omit<
    UseQueryOptions<TResponseWithQData<IContactPayload[]>, Error>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<TResponseWithQData<IContactPayload[]>, Error> & {
  refetchWithQuery: (params: Partial<QData>) => void;
} => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });

  const queryResult = useQuery<TResponseWithQData<IContactPayload[]>, Error>({
    queryKey: [API_ENDPOINTS.CONTACT_LIST, { ...queryParams, total: null }],
    queryFn: async () => {
      const res = await listContact(queryParams);
      return res.data;
    },
    select: (res) => ({
      ...res,
      data: res.data.map(x => ({ key: x._id, ...x })),
      qData: { ...queryParams, total: res.extra?.total }
    }),
    initialData: {
      data: [],
      success: true,
      message: '',
      code: 200,
      qData: { page: 1, limit: 20 },
      extra: { page: 1, limit: 20, total: 0 },
    },
    staleTime: 300000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
    ...options,
  });

  return {
    ...queryResult,
    refetchWithQuery: (params: Partial<QData>) => {
      setQueryParams((prev: any) => ({ ...prev, ...params }));
    },
  };
};

export const useContactDelete = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string) => deleteContact({ ids }),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.CONTACT_LIST],
        exact: false,
      });
    },
  });
};